import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Command as CommandPrimitive } from "cmdk";
import { useVirtualizer } from "@tanstack/react-virtual";
import { AnimatePresence, motion } from "motion/react";
import { Loader2, Search, X } from "lucide-react";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/modern/components/ui/popover";
import { Command, CommandList } from "@/modern/components/ui/command";
import {
  ENTITY_FINDER_EXPANDED_WIDTH_CLS,
  ENTITY_FINDER_INPUT_ANIMATION_MS,
  ENTITY_FINDER_INPUT_CLS,
  ENTITY_FINDER_KBD_CLS,
  ENTITY_FINDER_HELPER_CLS,
  ENTITY_FINDER_LIST_BOTTOM_PADDING,
  ENTITY_FINDER_LIST_BOTTOM_PADDING_PX,
  ENTITY_FINDER_OPEN_DELAY_MS,
  ENTITY_FINDER_PANEL_FADE_MS,
  ENTITY_FINDER_POPOVER_CLOSE_MS,
  ENTITY_FINDER_ROW_HEIGHT,
  SECTION_HEADER_HEIGHT,
} from "@/modern/components/entity-finder/constants/layout";
import { getConfigEntry } from "@/modern/components/entity-finder/constants/config";
import { EntityFinderScopeChips } from "@/modern/components/entity-finder/components/EntityFinderScopeChips";
import { EntityFinderCreateActions } from "@/modern/components/entity-finder/components/EntityFinderCreateActions";
import { EntityFinderList, EntityFinderRecentsList } from "@/modern/components/entity-finder/components/EntityFinderList";
import { isEditableTarget, useEntityFinder } from "@/modern/components/entity-finder/hooks/useEntityFinder";
import type { EntityFinderKind } from "@/modern/components/entity-finder/types";

interface SwivuEntityFinderProps {
  className?: string;
}

function KbdHint({ children }: { children: ReactNode }) {
  return (
    <kbd className={cn("pointer-events-none inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1", ENTITY_FINDER_KBD_CLS)}>
      {children}
    </kbd>
  );
}

const FINDER_PANEL_TRANSITION = { duration: ENTITY_FINDER_PANEL_FADE_MS / 1000, ease: "easeOut" as const };
const FINDER_INPUT_REVEAL_TRANSITION = {
  duration: ENTITY_FINDER_INPUT_ANIMATION_MS / 1000,
  ease: [0.22, 1, 0.36, 1] as const,
};

function getMacPlatform() {
  return (
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
  );
}

export function SwivuEntityFinder({ className }: SwivuEntityFinderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const justSelectedRef = useRef(false);
  const isFocusedRef = useRef(false);
  const isClosingRef = useRef(false);
  const closeTimeoutRef = useRef<number>();

  const [isFocused, setIsFocused] = useState(false);
  const [inputRevealed, setInputRevealed] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const isMac = useSyncExternalStore(
    () => () => {},
    getMacPlatform,
    () => false
  );
  const modKeyLabel = isMac ? "⌘" : "Ctrl";
  const searchShortcutLabel = isMac ? "⌘F" : "Ctrl+F";

  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current != null) window.clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const {
    query,
    setQuery,
    debouncedQuery,
    listEntries,
    recents,
    scopes,
    addScope,
    removeScope,
    clearScopes,
    isExpanded,
    expandAndFocus,
    collapse,
    isExpandedRef,
    selectItem,
    createOnUnmatchedOptions,
    runCreateOnUnmatched,
    isLoadingSettings,
    settingsReady,
    finderContext,
    totalResultCount,
  } = useEntityFinder({ inputRef, isFocused });

  const openDelayRef = useRef<number>();

  useEffect(() => {
    if (!isExpanded) {
      setInputRevealed(false);
      setPopoverOpen(false);
      isClosingRef.current = false;
      if (openDelayRef.current != null) window.clearTimeout(openDelayRef.current);
      return;
    }

    openDelayRef.current = window.setTimeout(() => {
      openDelayRef.current = undefined;
      setInputRevealed(true);
    }, ENTITY_FINDER_OPEN_DELAY_MS);

    return () => {
      if (openDelayRef.current != null) window.clearTimeout(openDelayRef.current);
    };
  }, [isExpanded]);

  const showDropdown = popoverOpen && isExpanded && isFocused;
  const hasQuery = query.trim().length > 0;
  const hasDebouncedQuery = debouncedQuery.trim().length > 0;
  const isIdleSearch = !hasQuery;
  const isDebouncing = hasQuery && query.trim() !== debouncedQuery;

  const [listScrollElement, setListScrollElement] = useState<HTMLDivElement | null>(null);

  const setListScrollRef = useCallback((node: HTMLDivElement | null) => {
    setListScrollElement(node);
  }, []);

  const rowVirtualizer = useVirtualizer({
    count: listEntries.length,
    getScrollElement: () => listScrollElement,
    estimateSize: (index) => {
      const row = listEntries[index];
      if (row.type === "header") return SECTION_HEADER_HEIGHT;
      const config = getConfigEntry(row.configType);
      if (!config) return ENTITY_FINDER_ROW_HEIGHT;
      if (row.type === "setting") return config.estimateRowHeight(row.item);
      return config.estimateRowHeight(row.item);
    },
    overscan: 10,
    paddingEnd: ENTITY_FINDER_LIST_BOTTOM_PADDING_PX,
  });

  useLayoutEffect(() => {
    if (!listScrollElement || listEntries.length === 0) return;
    rowVirtualizer.measure();
  }, [listScrollElement, listEntries.length, debouncedQuery, scopes, rowVirtualizer]);

  useEffect(() => {
    if (listScrollElement) listScrollElement.scrollTop = 0;
  }, [debouncedQuery, scopes, listEntries.length, listScrollElement]);

  const handleInputAnimationComplete = useCallback(() => {
    if (!isExpandedRef.current) return;

    if (inputRevealed) {
      if (isFocusedRef.current) setPopoverOpen(true);
      return;
    }

    if (!isClosingRef.current) return;
    isClosingRef.current = false;
    collapse();
  }, [collapse, inputRevealed, isExpandedRef]);

  const beginClose = useCallback(() => {
    if (isClosingRef.current) return;

    if (closeTimeoutRef.current != null) window.clearTimeout(closeTimeoutRef.current);

    setIsFocused(false);
    setPopoverOpen(false);
    inputRef.current?.blur();

    isClosingRef.current = true;
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = undefined;
      setInputRevealed(false);
    }, ENTITY_FINDER_POPOVER_CLOSE_MS);
  }, []);

  const handleSelect = useCallback(
    (item: Parameters<typeof selectItem>[0], configType: EntityFinderKind) => {
      justSelectedRef.current = true;
      selectItem(item, configType);
      beginClose();
    },
    [selectItem, beginClose]
  );

  const handleCreate = useCallback(
    (entry: Parameters<typeof runCreateOnUnmatched>[0]) => {
      justSelectedRef.current = true;
      runCreateOnUnmatched(entry);
      beginClose();
    },
    [runCreateOnUnmatched, beginClose]
  );

  const handleCancel = useCallback(() => {
    beginClose();
  }, [beginClose]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (isClosingRef.current) return;
    handleCancel();
  }, [handleCancel]);

  const handleValueChange = useCallback(
    (next: string) => {
      setQuery(next);
      setIsFocused(true);
    },
    [setQuery]
  );

  const handleClearQuery = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      setQuery("");
      inputRef.current?.focus();
    },
    [setQuery]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "f") return;

      if (isExpandedRef.current) {
        e.preventDefault();
        e.stopImmediatePropagation();
        beginClose();
        return;
      }

      if (isEditableTarget(document.activeElement)) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      expandAndFocus();
      setIsFocused(true);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [expandAndFocus, isExpandedRef, beginClose]);

  const showSearchLoading =
    (isDebouncing || (hasDebouncedQuery && !finderContext)) && hasQuery;
  const showResults =
    hasQuery &&
    hasDebouncedQuery &&
    !showSearchLoading &&
    finderContext &&
    listEntries.length > 0;
  const showEmptyState =
    hasQuery &&
    hasDebouncedQuery &&
    !showSearchLoading &&
    finderContext &&
    totalResultCount === 0;
  const showSearchPending =
    hasQuery && !isIdleSearch && !showSearchLoading && !showResults && !showEmptyState;

  return (
    <div className={cn("relative size-9 shrink-0", className)}>
      {!isExpanded && (
        <Button
          variant="outline"
          size="icon"
          onClick={expandAndFocus}
          aria-label={`Search entities (${searchShortcutLabel})`}
          className="size-9 rounded-lg shadow-sm transition-shadow hover:shadow-md"
        >
          <Search className="size-[1.125rem]" />
        </Button>
      )}

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="entity-finder-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: inputRevealed ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={FINDER_PANEL_TRANSITION}
            className={cn(
              "absolute right-0 top-0 z-20",
              ENTITY_FINDER_EXPANDED_WIDTH_CLS
            )}
          >
            <Command shouldFilter={false} className="w-full overflow-visible bg-transparent">
              <Popover open={showDropdown}>
                <motion.div
                  initial={false}
                  animate={{ scaleX: inputRevealed ? 1 : 0, opacity: inputRevealed ? 1 : 0.85 }}
                  transition={FINDER_INPUT_REVEAL_TRANSITION}
                  style={{ transformOrigin: "right center" }}
                  className="origin-right overflow-hidden will-change-transform"
                  onAnimationComplete={handleInputAnimationComplete}
                >
                  <PopoverAnchor asChild>
                    <div className="relative w-full">
                      <Search className="pointer-events-none absolute left-4 top-1/2 size-[1.125rem] -translate-y-1/2 text-muted-foreground" />
                      <CommandPrimitive.Input
                        ref={inputRef}
                        value={query}
                        onValueChange={handleValueChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            handleCancel();
                          }
                          if (e.key === "Tab" && showDropdown) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="Search…"
                        autoComplete="off"
                        aria-expanded={showDropdown}
                        className={cn(
                          "flex h-11 w-full border border-input/80 bg-background py-2 pl-11 shadow-sm outline-none",
                          ENTITY_FINDER_INPUT_CLS,
                          "placeholder:text-muted-foreground",
                          "ring-offset-background transition-[border-radius,box-shadow,border-color] duration-150",
                          "focus:border-ring/50 focus:shadow-md focus:ring-2 focus:ring-ring/20 focus:ring-offset-0",
                          showDropdown ? "rounded-t-xl rounded-b-none border-b-transparent" : "rounded-xl",
                          isDebouncing ? "pr-11" : query ? "pr-10" : isMac ? "pr-12" : "pr-[4.25rem]"
                        )}
                      />
                      {showSearchLoading ? (
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="size-4 animate-spin text-muted-foreground" />
                        </div>
                      ) : query ? (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleClearQuery}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          aria-label="Clear search"
                        >
                          <X size={14} />
                        </button>
                      ) : (
                        <div className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-1 sm:flex">
                          <KbdHint>{modKeyLabel}</KbdHint>
                          <KbdHint>F</KbdHint>
                        </div>
                      )}
                    </div>
                  </PopoverAnchor>
                </motion.div>

                <PopoverContent
                  className={cn(
                    ENTITY_FINDER_EXPANDED_WIDTH_CLS,
                    "z-[--swivu-bryntum-z-index] p-0 !bg-background",
                    "rounded-b-xl rounded-t-none border border-t-0 border-border bg-popover text-popover-foreground shadow-2xl",
                    "data-[state=open]:animate-in data-[state=closed]:animate-out",
                    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                    "data-[state=closed]:duration-100 data-[state=open]:duration-200",
                    "data-[side=bottom]:slide-in-from-top-1"
                  )}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  align="end"
                  sideOffset={0}
                >
                  <div className="flex max-h-[560px] flex-col">
                    {isLoadingSettings && !settingsReady && hasDebouncedQuery && (
                      <p className={cn("px-4 py-2", ENTITY_FINDER_HELPER_CLS)}>Loading settings…</p>
                    )}

                    <CommandList
                      ref={setListScrollRef}
                      className={cn(
                        "max-h-[min(32rem,56vh)] flex-1 overflow-y-auto py-0",
                        ENTITY_FINDER_LIST_BOTTOM_PADDING
                      )}
                    >
                      {isIdleSearch && finderContext && (
                        <EntityFinderRecentsList
                          items={recents}
                          query={debouncedQuery}
                          ctx={finderContext}
                          onSelect={handleSelect}
                        />
                      )}

                      {showSearchLoading && (
                        <div className={cn("flex items-center justify-center px-4 py-8", ENTITY_FINDER_HELPER_CLS)}>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Searching…
                        </div>
                      )}

                      {showEmptyState && (
                        <EntityFinderCreateActions
                          query={debouncedQuery}
                          options={createOnUnmatchedOptions}
                          onCreate={handleCreate}
                        />
                      )}

                      {showSearchPending && (
                        <div className={cn("flex items-center justify-center px-4 py-8", ENTITY_FINDER_HELPER_CLS)}>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Searching…
                        </div>
                      )}

                      {showResults && finderContext && (
                        <EntityFinderList
                          key={`${debouncedQuery}-${scopes.join(",")}`}
                          entries={listEntries}
                          rowVirtualizer={rowVirtualizer}
                          query={debouncedQuery}
                          ctx={finderContext}
                          onSelect={handleSelect}
                        />
                      )}
                    </CommandList>

                    <EntityFinderScopeChips
                      scopes={scopes}
                      onAdd={addScope}
                      onRemove={removeScope}
                      onClearAll={clearScopes}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </Command>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
